/** Font picker — every name shown in its own face.
 *
 *  A native <select> cannot do this: iOS Safari ignores a font on an <option>,
 *  so on an iPad all 25 names would look identical. Hence a real list.
 *
 *  The faces load ONLY when the list is opened, one stylesheet per family from
 *  our own domain. A fan page never comes near this component — it loads just
 *  the two faces its setup chose.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { FONT_CHOICES, FONT_GROUPS, fontStack, loadFont } from '../../lib/designTokens';

export const FontPicker = ({ value, onChange, label, testid }) => {
    const [open, setOpen] = useState(false);
    const box = useRef(null);
    const current = FONT_CHOICES.find((f) => f.key === (value || '')) || FONT_CHOICES[0];

    // Only on open, and spread over a few frames so 25 stylesheets do not land
    // in one burst while the list is animating in.
    useEffect(() => {
        if (!open) return undefined;
        const timers = FONT_GROUPS.flatMap((g) => g.fonts).map(
            (f, i) => setTimeout(() => loadFont(f.key), i * 20));
        return () => timers.forEach(clearTimeout);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', away);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', away);
            document.removeEventListener('keydown', esc);
        };
    }, [open]);

    const pick = (key) => {
        onChange(key);
        loadFont(key);
        setOpen(false);
    };

    return (
        <div className="relative" ref={box}>
            <button
                type="button" onClick={() => setOpen((o) => !o)}
                data-testid={testid}
                aria-haspopup="listbox" aria-expanded={open}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-left text-xs hover:border-foreground/30 transition-colors"
            >
                <span className="truncate">
                    <span className="text-muted-foreground">{label}: </span>
                    <span style={{ fontFamily: fontStack(current.key) || undefined }}>
                        {current.label}
                    </span>
                </span>
                <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div
                    role="listbox"
                    data-testid={`${testid}-list`}
                    className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto rounded-xl border border-border bg-popover shadow-xl p-1"
                >
                    <button
                        type="button" role="option" aria-selected={!value}
                        onClick={() => pick('')}
                        data-testid={`${testid}-option-default`}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs hover:bg-accent text-left"
                    >
                        Default (app font)
                        {!value && <Check size={14} className="text-emerald-500" />}
                    </button>
                    {FONT_GROUPS.map((group) => (
                        <div key={group.label}>
                            <p className="px-2.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {group.label}
                            </p>
                            {group.fonts.map((font) => (
                                <button
                                    key={font.key} type="button" role="option"
                                    aria-selected={value === font.key}
                                    onClick={() => pick(font.key)}
                                    data-testid={`${testid}-option-${font.key}`}
                                    className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg hover:bg-accent text-left"
                                >
                                    <span className="text-base leading-tight truncate"
                                          style={{ fontFamily: font.stack }}>
                                        {font.label}
                                    </span>
                                    {value === font.key && <Check size={14} className="shrink-0 text-emerald-500" />}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FontPicker;
